import { useAppState, useUserState } from '@/core/state/react'
import { appActions, appSelectors } from '@/core/state/app'
import { useUnitConversions } from '@/react/hooks/useUnitConversions'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/react/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/react/ui/dropdown-menu'
import { MARKETING_URL } from '@/react/router/leave'
import { logoutAndLeave } from './logout'

/**
 * App.vue's <v-app-bar> in React: the logo, the demo ROI chip, the global unit
 * toggle, the home link and the account menu. The markup and class names mirror
 * the Vuetify tree so frame.css can carry its boxes verbatim and the Phase 0
 * visual baselines still match.
 */
export function AppBar() {
  const isDemo = useAppState(appSelectors.isDemoExperience)
  const scenarioId = useAppState((s) => s.currentScenarioId)
  const workspaceId = useAppState((s) => s.workspaceId)
  const globalUnit = useAppState((s) => s.globalDisplayUnit)
  const email = useUserState((s) => s.email)
  const { availableUnits, baseUnit } = useUnitConversions(scenarioId, workspaceId)
  const effectiveUnit = globalUnit || baseUnit || null

  return (
    <header className="scvc-app-bar app-bar-custom" data-testid="app-bar">
      <div className="scvc-app-bar__content">
        <div className="app-bar-inner">
          <div className="brand-container">
            <img src="/FullLogo_NoBuffer.png" alt="Continuous Design" className="brand-logo" />
          </div>

          <div className="scvc-spacer" />

          <div className="scvc-bar-actions">
            {isDemo && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="scvc-chip scvc-chip--success-elevated roi-chip" data-testid="roi-chip">
                    <span className="scvc-chip__prepend">
                      <i className="mdi mdi-trending-down scvc-icon" aria-hidden="true" />
                    </span>
                    <span className="font-bold">15% Savings</span>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="scvc-tooltip">
                  <div className="text-center p-2">
                    <div className="font-bold mb-1">Optimized Network Results</div>
                    <div className="scvc-text-caption">
                      Saves <strong>$14.1M annually</strong> vs baseline
                    </div>
                    <div className="scvc-text-caption mt-1">See delta details in Total Cost card →</div>
                  </div>
                </TooltipContent>
              </Tooltip>
            )}

            {availableUnits.length > 1 && (
              <DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <button type="button" className="scvc-chip scvc-chip--unit unit-chip" data-testid="unit-chip">
                        <span className="scvc-chip__prepend">
                          <i className="mdi mdi-scale-balance scvc-icon" aria-hidden="true" />
                        </span>
                        <span className="scvc-chip__label">{effectiveUnit || 'Auto'}</span>
                        <span className="scvc-chip__append">
                          <i className="mdi mdi-chevron-down scvc-icon" style={{ fontSize: 14 }} aria-hidden="true" />
                        </span>
                      </button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="scvc-tooltip">
                    Display Unit
                  </TooltipContent>
                </Tooltip>
                <DropdownMenuContent align="end" sideOffset={4} className="scvc-menu min-w-[160px]">
                  <DropdownMenuLabel className="scvc-menu__subheader">Display Unit</DropdownMenuLabel>
                  {availableUnits.map((unit) => (
                    <DropdownMenuItem
                      key={unit}
                      className="scvc-menu__item capitalize"
                      data-active={effectiveUnit === unit ? '' : undefined}
                      onSelect={() => appActions.setGlobalDisplayUnit(unit)}
                    >
                      <span className="scvc-menu__title">{unit}</span>
                      {unit === baseUnit && <span className="scvc-chip scvc-chip--base">base</span>}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <span className="scvc-divider opacity-20" role="separator" aria-orientation="vertical" />

            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href={MARKETING_URL}
                  className="scvc-icon-btn action-btn"
                  aria-label="Back to Home"
                  data-testid="home-link"
                >
                  <i className="mdi mdi-home-outline scvc-icon" style={{ fontSize: 22 }} aria-hidden="true" />
                </a>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="scvc-tooltip">
                Back to Home
              </TooltipContent>
            </Tooltip>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="scvc-icon-btn action-btn ml-1" aria-label="Account" data-testid="user-menu">
                  <span className="scvc-avatar">
                    <i className="mdi mdi-account scvc-icon" style={{ fontSize: 20 }} aria-hidden="true" />
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={8} className="scvc-menu min-w-[200px]">
                {email && (
                  <>
                    <div className="scvc-menu__item" data-testid="user-email">
                      <i className="mdi mdi-account-circle scvc-icon scvc-menu__prepend" aria-hidden="true" />
                      <span className="scvc-menu__title scvc-text-body-2">{email}</span>
                    </div>
                    <DropdownMenuSeparator className="scvc-menu__separator" />
                  </>
                )}
                <DropdownMenuItem
                  className="scvc-menu__item scvc-menu__item--error"
                  onSelect={() => void logoutAndLeave()}
                  data-testid="logout"
                >
                  <i className="mdi mdi-logout scvc-icon scvc-menu__prepend" aria-hidden="true" />
                  <span className="scvc-menu__title">Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  )
}
